/**
 * RTMPose → Canonical skeleton normalizer.
 *
 * RTMPose ships multiple keypoint formats; this normalizer covers the
 * COCO-17 layout (the default for `rtmpose-{t,m,l}`). Pure-deterministic
 * geometry: no learning, no time-series state. Same input MUST produce
 * bit-identical output.
 *
 * docs/architecture-spec.md §5.1, §4.3
 * docs/migration-plan.md Phase 11
 */

import type { Body, Landmark } from '../../canonical/Landmark';
import { FACE_JOINT_KEYS, type CanonicalJointKey } from '../../canonical/JointKeys';

/** COCO-17 native keypoint names, in index order. */
const NATIVE_NAME_BY_INDEX: readonly string[] = [
  'nose',
  'left_eye',
  'right_eye',
  'left_ear',
  'right_ear',
  'left_shoulder',
  'right_shoulder',
  'left_elbow',
  'right_elbow',
  'left_wrist',
  'right_wrist',
  'left_hip',
  'right_hip',
  'left_knee',
  'right_knee',
  'left_ankle',
  'right_ankle',
];

/** COCO-17 → CanonicalJointKey direct mapping. The nose is taken as the
 *  representative head landmark, matching the MediaPipe normalizer. */
const DIRECT_MAPPING: readonly (CanonicalJointKey | null)[] = [
  'head', // 0 nose
  'leftEye', // 1
  'rightEye', // 2
  'leftEar', // 3
  'rightEar', // 4
  'leftShoulder', // 5
  'rightShoulder', // 6
  'leftElbow', // 7
  'rightElbow', // 8
  'leftWrist', // 9
  'rightWrist', // 10
  'leftHip', // 11
  'rightHip', // 12
  'leftKnee', // 13
  'rightKnee', // 14
  'leftAnkle', // 15
  'rightAnkle', // 16
];

export interface RTMPoseRawKeypoint {
  /** Normalised x in source image space (0..1). */
  x: number;
  /** Normalised y in source image space (0..1). */
  y: number;
  /** SimCC peak (post-sigmoid) in [0, 1]. */
  score: number;
}

export interface RTMPoseNormalizerOptions {
  /** Native pixel dimensions of the source frame. */
  sourceWidthPx: number;
  sourceHeightPx: number;
  pixelAspect?: number;
  frameLocalId: number;
  persistentTrackingId?: number | null;
  /** Skeleton identifier. e.g. "rtmpose-coco17". */
  skeletonId: string;
  /** When true, omit face PII landmarks (head, eyes, ears, mouth). */
  excludeFaceLandmarks?: boolean;
}

/** Convert a single RTMPose-COCO17 detection into a canonical Body. */
export function normalizeRTMPoseBody(
  raw: readonly RTMPoseRawKeypoint[],
  opts: RTMPoseNormalizerOptions,
): Body {
  const { sourceWidthPx, sourceHeightPx, pixelAspect = 1 } = opts;
  const excludeFace = opts.excludeFaceLandmarks ?? false;
  const out: Landmark[] = [];

  for (let i = 0; i < raw.length && i < DIRECT_MAPPING.length; i += 1) {
    const lm = raw[i];
    const jointKey = DIRECT_MAPPING[i];
    if (excludeFace && jointKey && (FACE_JOINT_KEYS as readonly string[]).includes(jointKey)) {
      continue;
    }
    out.push(
      buildLandmark(
        lm,
        NATIVE_NAME_BY_INDEX[i],
        i,
        jointKey,
        false,
        'sensor',
        sourceWidthPx,
        sourceHeightPx,
        pixelAspect,
      ),
    );
  }

  // Synthesise pelvis = midpoint(leftHip, rightHip), and a coarse spine
  // ladder from midShoulder ↔ pelvis. RTMPose-COCO17 has no native
  // spine / hand / foot landmarks; Core modules fall back to these
  // synthesised joints (matching the MediaPipe normalizer's output set).
  const lHip = raw[11];
  const rHip = raw[12];
  const lSh = raw[5];
  const rSh = raw[6];

  if (lHip && rHip) {
    out.push(
      synthMidpoint(
        lHip,
        rHip,
        'pelvis',
        100,
        'pelvis_midpoint',
        sourceWidthPx,
        sourceHeightPx,
        pixelAspect,
      ),
    );
  }

  if (lHip && rHip && lSh && rSh) {
    const midShoulder = avgKeypoint(lSh, rSh);
    const pelvis = avgKeypoint(lHip, rHip);
    const spineMid = avgKeypoint(midShoulder, pelvis);
    const spineUpper = avgKeypoint(midShoulder, spineMid);

    out.push(
      buildLandmark(
        midShoulder,
        'mid_shoulder',
        101,
        'neckBase',
        true,
        'midpoint',
        sourceWidthPx,
        sourceHeightPx,
        pixelAspect,
      ),
    );
    out.push(
      buildLandmark(
        pelvis,
        'pelvis_synth',
        102,
        'spineLower',
        true,
        'midpoint',
        sourceWidthPx,
        sourceHeightPx,
        pixelAspect,
      ),
    );
    out.push(
      buildLandmark(
        spineMid,
        'spine_mid',
        103,
        'spineMid',
        true,
        'midpoint',
        sourceWidthPx,
        sourceHeightPx,
        pixelAspect,
      ),
    );
    out.push(
      buildLandmark(
        spineUpper,
        'spine_upper',
        104,
        'spineUpper',
        true,
        'midpoint',
        sourceWidthPx,
        sourceHeightPx,
        pixelAspect,
      ),
    );
  }

  return {
    frameLocalId: opts.frameLocalId,
    persistentTrackingId: opts.persistentTrackingId ?? null,
    landmarks: out,
    segmentation: null,
    skeletonId: opts.skeletonId,
  };
}

function buildLandmark(
  k: RTMPoseRawKeypoint,
  nativeName: string,
  nativeIndex: number,
  jointKey: CanonicalJointKey | null,
  synthesized: boolean,
  derivation: 'sensor' | 'midpoint' | 'bone-end-projection',
  sourceWidthPx: number,
  sourceHeightPx: number,
  pixelAspect: number,
): Landmark {
  return {
    jointKey,
    raw: { nativeName, nativeIndex },
    pos2D: {
      xPx: k.x * sourceWidthPx,
      yPx: k.y * sourceHeightPx,
      sourceWidthPx,
      sourceHeightPx,
      pixelAspect,
    },
    pos3DWorld: null,
    pos3DLocal: null,
    confidence: {
      score: k.score,
      visibility: null,
      occluded: k.score < 0.3,
    },
    uncertainty: { sigmaPx: null, sigmaM: null },
    origin: { synthesized, derivation: synthesized ? derivation : 'sensor' },
  };
}

function avgKeypoint(a: RTMPoseRawKeypoint, b: RTMPoseRawKeypoint): RTMPoseRawKeypoint {
  return {
    x: (a.x + b.x) * 0.5,
    y: (a.y + b.y) * 0.5,
    score: Math.min(a.score, b.score),
  };
}

function synthMidpoint(
  a: RTMPoseRawKeypoint,
  b: RTMPoseRawKeypoint,
  jointKey: CanonicalJointKey,
  nativeIndex: number,
  nativeName: string,
  sourceWidthPx: number,
  sourceHeightPx: number,
  pixelAspect: number,
): Landmark {
  return buildLandmark(
    avgKeypoint(a, b),
    nativeName,
    nativeIndex,
    jointKey,
    true,
    'midpoint',
    sourceWidthPx,
    sourceHeightPx,
    pixelAspect,
  );
}
